import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { 
    PlayIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../utils/api';

function TestDataPanel() {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [clearLoading, setClearLoading] = useState(false);

    const insertTestData = async () => {
        if (!window.confirm('Test verileri eklenecek. Bu işlem mevcut test verilerini temizleyip yenilerini ekleyecek. Devam etmek istiyor musunuz?')) {
            return;
        }

        try {
            setLoading(true);
            
            const response = await api.post('/api/test-data/insert-test-data', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('API Response:', response.data); // Debug için
            
            if (response.data && response.data.success) {
                toast.success('Test verileri başarıyla eklendi!');
                console.log('Eklenen test verileri:', response.data.data);
            } else {
                console.error('API Response Error:', response.data);
                throw new Error(response.data?.error || 'Test verileri eklenirken hata oluştu');
            }
        } catch (error) {
            console.error('Test verileri ekleme hatası:', error);
            toast.error(error.response?.data?.error || error.message || 'Test verileri eklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const clearTestData = async () => {
        if (!window.confirm('Tüm test verileri silinecek. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?')) {
            return;
        }

        try {
            setClearLoading(true);
            
            const response = await api.delete('/api/test-data/clear-test-data', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                toast.success('Test verileri başarıyla temizlendi!');
            } else {
                throw new Error(response.data.error || 'Test verileri temizlenirken hata oluştu');
            }
        } catch (error) {
            console.error('Test verileri temizleme hatası:', error);
            toast.error(error.response?.data?.error || error.message || 'Test verileri temizlenirken hata oluştu');
        } finally {
            setClearLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Test Verileri Yönetimi</h2>
                        <p className="text-gray-600 mt-2">Analytics sistemini test etmek için örnek veriler ekleyin</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={insertTestData}
                            disabled={loading}
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? (
                                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <PlayIcon className="h-4 w-4 mr-2" />
                            )}
                            {loading ? 'Ekleniyor...' : 'Test Verileri Ekle'}
                        </button>
                        <button
                            onClick={clearTestData}
                            disabled={clearLoading}
                            className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {clearLoading ? (
                                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <TrashIcon className="h-4 w-4 mr-2" />
                            )}
                            {clearLoading ? 'Temizleniyor...' : 'Test Verilerini Temizle'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Bilgi Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Test Verileri Hakkında */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-start">
                        <CheckCircleIcon className="h-6 w-6 text-blue-600 mt-1 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">Test Verileri İçeriği</h3>
                            <ul className="text-blue-800 space-y-1 text-sm">
                                <li>• <strong>9 Kategori:</strong> 3 ana kategori + 6 alt kategori</li>
                                <li>• <strong>8 Test Ürünü:</strong> Farklı kategorilerde ürünler</li>
                                <li>• <strong>5 Test Siparişi:</strong> Tamamlanmış durumda siparişler</li>
                                <li>• <strong>9 Sipariş Kalemi:</strong> Farklı ürünlerden satışlar</li>
                                <li>• <strong>Ürün-Kategori İlişkileri:</strong> Analytics için gerekli bağlantılar</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Uyarılar */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-1 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Önemli Notlar</h3>
                            <ul className="text-yellow-800 space-y-1 text-sm">
                                <li>• Test verileri "Test" ön eki ile başlar</li>
                                <li>• Mevcut test verileri otomatik temizlenir</li>
                                <li>• Gerçek verileriniz etkilenmez</li>
                                <li>• Analytics sonuçları hemen görünür</li>
                                <li>• Test tamamlandıktan sonra verileri temizleyebilirsiniz</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Test Senaryoları */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Senaryoları</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Kategori Performansı</h4>
                        <p className="text-sm text-gray-600">
                            Farklı kategorilerde satış performansını test edin. 
                            Bebek giyim, kız çocuk ve erkek çocuk kategorilerinde 
                            çeşitli ürünler ve satışlar bulunur.
                        </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Gelir Analizi</h4>
                        <p className="text-sm text-gray-600">
                            Toplam gelir, ortalama sipariş değeri ve kategori bazlı 
                            gelir dağılımını analiz edin. Farklı fiyat aralıklarında 
                            ürünler mevcuttur.
                        </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Büyüme Trendi</h4>
                        <p className="text-sm text-gray-600">
                            Son günlerde yapılan satışlarla büyüme oranlarını 
                            test edin. Siparişler farklı tarihlerde oluşturulmuştur.
                        </p>
                    </div>
                </div>
            </div>

            {/* Test Adımları */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Adımları</h3>
                <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    <li><strong>"Test Verileri Ekle"</strong> butonuna tıklayın</li>
                    <li><strong>"Gelişmiş Analytics"</strong> sekmesine gidin</li>
                    <li>Özet kartlarında verilerin göründüğünü kontrol edin</li>
                    <li>Detaylı kategori tablosunu inceleyin</li>
                    <li>Gelir dağılım grafiğini kontrol edin</li>
                    <li>Farklı zaman aralıklarını test edin (7, 30, 90 gün)</li>
                    <li>Test tamamlandıktan sonra <strong>"Test Verilerini Temizle"</strong> butonunu kullanın</li>
                </ol>
            </div>
        </div>
    );
}

export default TestDataPanel;